import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { messages, type Locale } from './messages';

type I18nContextValue = {
    locale: Locale;
    t: (key: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
    locale,
    children,
}: {
    locale: Locale;
    children: ReactNode;
}) {
    const t = useCallback(
        (key: string) => {
            const pack = messages[locale] ?? messages.en;
            return pack[key] ?? messages.en[key] ?? key;
        },
        [locale],
    );

    const value = useMemo(() => ({ locale, t }), [locale, t]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error('useI18n must be used within I18nProvider');
    }
    return ctx;
}
